<script>
import { CdrText } from '@rei/cedar';

import { parseISO } from 'date-fns';

export default {
  name: 'HoursComponent',
  components: {
    CdrText,
  },
  props: {
    notAvailableCopy: { type: String, default: 'Closed' },
    title: { type: String, default: 'REI Help Center' },
    hoursModel: {
      type: Object,
      default() {
        return {};
      },
    },
    hoursTitle: { type: String, default: 'Customer service hours' },
    centerClass: { type: String, default: '' },
  },
  data() {
    return {
      timeZone: '',
    };
  },
  computed: {
    events() {
      return this.hoursModel.retailStoreHoursEvents || [];
    },
    normal() {
      return this.formatNormal(this.filteredByType('NormalHours'));
    },
    special() {
      return this.formatSpecial(this.filteredByType('SpecialHours'));
    },
    classObject() {
      return {
        'cdr-mb-space-one-x': !!this.centerClass,
      };
    },
  },
  created() {
    this.timeZone = 'PT';
  },
  methods: {
    filteredByType(type) {
      return this.events.filter((event) => event.hoursType === type);
    },
    removeMinutes(hour = '') {
      return `${hour.split(':')[0]}${hour.split(' ')[1] || ''}`;
    },
    removeSpaces(content = '') {
      return content.replace(/\s/g, '');
    },
    formatDash(content) {
      return this.removeSpaces(content).replace('-', '&ndash;');
    },
    /**
     * This function ensures the normal store hours order in weekdays,
     * and aggregate weekend days when the hours are the same
     * @param normalDates {Array} - normal day hours
     * @returns {Array}
     */
    formatNormal(normalDates) {
      // Ensure days order
      const days = ['Mon - Fri', 'Sat', 'Sun'];
      const firstNormalDate = normalDates[0];
      const sorted = (firstNormalDate?.retailStoreHours || []).toSorted((a, b) => {
        if (days.indexOf(a.effectiveDay) <= days.indexOf(b.effectiveDay)) {
          return -1;
        }
        return 1;
      });
      // Aggregate weekend hours if they are the same and store has same open/closed flag
      const weekends = sorted.slice(-2);
      const { openAt, closeAt, storeClosed } = weekends[0];
      const weekendsHasSameHours = weekends.every((entry) => entry.openAt === openAt && entry.closeAt === closeAt && entry.storeClosed === storeClosed);

      const aggregated = { ...weekends[0], effectiveDay: 'Sat - Sun' };
      const retailStoreHours = weekendsHasSameHours
        ? [...[sorted[0]], aggregated]
        : sorted;
      return [{ ...firstNormalDate, retailStoreHours }];
    },
    formattedEffectiveDay(storeHour) {
      const { effectiveDate, effectiveDay } = storeHour;
      const storeDateString = (effectiveDate.split('T')[0]).split('-');
      const storeDate = parseISO(storeDateString.join('-'));
      const month = Intl.DateTimeFormat('en-US', { month: 'numeric' }).format(storeDate);
      const day = Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(storeDate);
      return `${effectiveDay} ${month}/${day}`;
    },
    /**
     * This function formats the special dates with the desired template using formattedEffectiveDay helper
     * @param specialDates {Array} - special date hours
     * @returns {Array}
     */
    formatSpecial(specialDates) {
      // Special dates formatting to display the short month and day
      return specialDates.map((date) => {
        const retailStoreHours = (date?.retailStoreHours || []).map((storeHour) => ({
          ...storeHour,
          effectiveDay: this.formattedEffectiveDay(storeHour),
        }));
        return {
          ...date,
          retailStoreHours,
        };
      });
    },
  },
};
</script>
<template>
  <div>
    <cdr-text
      tag="h3"
      :class="centerClass"
      tabindex="0"
    >
      <span class="contact-us-hours-title">
        {{ hoursTitle }}:
      </span>
    </cdr-text>
    <div
      v-for="(event, index) in normal"
      :key="`normal-event-${index}`"
      class="contact-us-event-div"
      tabindex="0"
    >
      <div
        v-for="(normalHours, index1) in event.retailStoreHours"
        :key="`normal-hours-${index1}`"
        class="cdr-display-flex"
        style="align-items: center"
        :class="centerClass"
      >
        <cdr-text
          class="contact-us-effective-day"
        >
          <span v-html="formatDash(normalHours.effectiveDay)" />,
        </cdr-text>
        <cdr-text>
          {{ !normalHours.storeClosed ? removeMinutes(normalHours.openAt): 'Closed' }}
        </cdr-text>
        <cdr-text
          v-if="!normalHours.storeClosed"
        >
          &ndash;{{ removeMinutes(normalHours.closeAt) }}
        </cdr-text>
        <cdr-text
          v-if="!normalHours.storeClosed"
        >
          <span class="contact-us-time-zone">
            {{ timeZone }}
          </span>
        </cdr-text>
      </div>
    </div>
    <cdr-text
      v-if="!!special.length"
      tag="h3"
      :class="centerClass"
      tabindex="0"
    >
      <span class="contact-us-special-hours-heading">
        Special Hours
      </span>
    </cdr-text>
    <div
      v-for="(event, index) in special"
      :key="`special-event-${index}`"
      class="contact-us-event-div"
      tabindex="0"
    >
      <cdr-text
        tag="h4"
        :class="centerClass"
      >
        <span class="contact-us-event-header">
          {{ event.eventName }}
        </span>
      </cdr-text>
      <div
        v-for="(specialHour, index2) in event.retailStoreHours"
        :key="`special-event-hours-${index2}`"
        class="cdr-display-flex"
        style="align-items: center"
        :class="centerClass"
      >
        <cdr-text>
          <span class="contact-us-special-hour-day">
            {{ specialHour.effectiveDay }},
          </span>
        </cdr-text>
        <cdr-text>
          {{ !specialHour.storeClosed ? removeMinutes(specialHour.openAt) : notAvailableCopy }}
        </cdr-text>
        <cdr-text
          v-if="!specialHour.storeClosed"
        >
          &ndash;{{ removeMinutes(specialHour.closeAt) }}
        </cdr-text>
        <cdr-text
          v-if="!specialHour.storeClosed"
        >
          <span class="contact-us-time-zone">
            {{ timeZone }}
          </span>
        </cdr-text>
      </div>
    </div>
  </div>
</template>
